import { useEffect, useState } from "react";
import type { WorkCondition, WorkProfessionalReview } from "@/types/work.types";
import { getWorkProfessionalReviews } from "@/services/work.service";
import { logError } from "@/infrastructure/observability/error-logging.service";
import styles from "./work-edition-info.module.scss";

type TabId = "details" | "reviews" | "awards";

interface WorkEditionInfoProps {
  condition: WorkCondition;
}

const WorkEditionInfo = ({ condition }: WorkEditionInfoProps) => {
  const [selectedTab, setSelectedTab] = useState<TabId>("details");
  const [reviews, setReviews] = useState<WorkProfessionalReview[] | null>(null);
  const details = condition.editionDetailItems ?? [];

  useEffect(() => {
    if (selectedTab !== "reviews" || reviews !== null) return;
    let cancelled = false;
    getWorkProfessionalReviews(condition.idAmazon)
      .then((result) => {
        if (!cancelled) setReviews(result);
      })
      .catch((error) => {
        logError(error, { context: "WorkEditionInfo:professionalReviews" });
        if (!cancelled) setReviews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [condition.idAmazon, reviews, selectedTab]);

  const tabs: { id: TabId; label: string }[] = [
    { id: "details", label: "Edition Details" },
    { id: "reviews", label: "Professional Reviews" },
    { id: "awards", label: "Awards" },
  ];

  return (
    <section className={styles.root} aria-label="Edition information">
      <div
        className={styles.tabs}
        role="tablist"
        aria-label="Edition information"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selectedTab === tab.id}
            aria-controls={`edition-panel-${tab.id}`}
            id={`edition-tab-${tab.id}`}
            onClick={() => setSelectedTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div
        className={styles.panel}
        role="tabpanel"
        id={`edition-panel-${selectedTab}`}
        aria-labelledby={`edition-tab-${selectedTab}`}
      >
        {selectedTab === "details" &&
          (details.length > 0 ? (
            <dl className={styles.details}>
              {details.map((row) => (
                <div key={row.key}>
                  <dt>{row.label}:</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p>Edition details are not available for this copy.</p>
          ))}
        {selectedTab === "reviews" && reviews === null && (
          <p>Loading professional reviews…</p>
        )}
        {selectedTab === "reviews" &&
          reviews !== null &&
          (reviews.length > 0 ? (
            <div className={styles.professionalReviews}>
              {reviews.map((review) => (
                <article key={`${review.reviewerName}-${review.review}`}>
                  {review.fromOtherEdition && (
                    <em>This review is from another edition.</em>
                  )}
                  <p>{review.review}</p>
                  {review.reviewerName && (
                    <strong>—{review.reviewerName}</strong>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <p>
              We don&apos;t have a record of any professional reviews for this
              work.
            </p>
          ))}
        {selectedTab === "awards" && (
          <p>We don&apos;t have a record of any awards for this work.</p>
        )}
      </div>
    </section>
  );
};

export default WorkEditionInfo;
